import React, { useEffect, useState } from "react";
import axios from "axios";
import CONFIG from "../../config";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Search,
  Calendar,
  IndianRupee,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Filter,
  Loader2,
  AlertCircle,
  Download,
  Printer
} from "lucide-react";

const baseURL = CONFIG.BASE_URL;

const formatCurrency = (amt) => {
  if (amt === null || amt === undefined) return "₹0.00";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  }).format(amt);
};

const formatDate = (dt) => {
  if (!dt) return "-";
  const d = new Date(dt);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
};

export default function PatientPaymentReport() {
  const { doctorInfo, token } = usePatient();
  const { theme, isDarkMode } = useTheme();

  const [activeTab, setActiveTab] = useState("patient"); // patient, monthly, annual

  // For Patient Report
  const [patientId, setPatientId] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportType, setReportType] = useState("custom");

  // For Monthly/Annual Reports
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [annualBreakdown, setAnnualBreakdown] = useState([]);

  // Data State
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, netBalance: 0 });

  const setRange = (type) => {
    setReportType(type);
    const end = new Date();
    const start = new Date();

    if (type === "weekly") {
      start.setDate(end.getDate() - 7);
    } else if (type === "monthly") {
      start.setMonth(end.getMonth() - 1);
    } else if (type === "yearly") {
      start.setFullYear(end.getFullYear() - 1);
    } else {
      return;
    }

    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(end.toISOString().slice(0, 10));
  };

  useEffect(() => {
    if (activeTab === "patient") {
      if (patientId || reportType !== "custom") {
        fetchPayments();
      }
    } else if (activeTab === "monthly") {
      fetchMonthlyReport();
    } else if (activeTab === "annual") {
      fetchAnnualReport();
    }
  }, [activeTab, startDate, endDate, patientId, selectedYear, selectedMonth]);

  const buildHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || token || ""}`,
    doctorId: doctorInfo?.MANDALI_ID || "",
    clinicId: doctorInfo?.CLINIC_ID || "",
    roleId: doctorInfo?.MANDALI_ROLE || "",
  });

  const computeTotals = (rows) => {
    let totalDebit = 0;
    let totalCredit = 0;

    rows.forEach((r) => {
      const charges = (Number(r.CONSULTING_CHARGES || 0) + Number(r.PANCHKARMA_CHARGES || 0) + Number(r.MEDICINE_CHARGES || 0));
      totalDebit += charges;
      totalCredit += Number(r.CURRENT_PAYMENT || 0);
    });

    setTotals({ totalDebit, totalCredit, netBalance: totalDebit - totalCredit });
  };

  const fetchPayments = async () => {
    setError(null);
    if (!startDate || !endDate) {
      setError("Please provide both start and end dates.");
      return;
    }

    setLoading(true);
    try {
      const s = `${startDate} 00:00:00`;
      const e = `${endDate} 23:59:59`;
      let url;

      if (patientId) {
        url = `${baseURL}/payments/search/filter?startDate=${encodeURIComponent(s)}&endDate=${encodeURIComponent(e)}&patientId=${encodeURIComponent(patientId)}`;
      } else {
        url = `${baseURL}/payments/search/filter?startDate=${encodeURIComponent(s)}&endDate=${encodeURIComponent(e)}`;
        if (doctorInfo?.MANDALI_ID) url += `&mandaliId=${doctorInfo.MANDALI_ID}`;
        if (doctorInfo?.CLINIC_ID) url += `&clinicId=${doctorInfo.CLINIC_ID}`;
      }

      const resp = await axios.get(url, { headers: buildHeaders() });
      const data = resp.data?.data || resp.data || [];
      const rows = Array.isArray(data) ? data : [];
      setPayments(rows);
      computeTotals(rows);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError(err.response?.data?.error || err.message || "Failed to fetch payments");
      setPayments([]);
      setTotals({ totalDebit: 0, totalCredit: 0, netBalance: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${baseURL}/payments/billing/monthly-transactions?year=${selectedYear}&month=${selectedMonth}`;
      const resp = await axios.get(url, { headers: buildHeaders() });
      const data = resp.data?.data || resp.data || [];
      setPayments(Array.isArray(data) ? data : []);

      const summaryUrl = `${baseURL}/payments/billing/monthly-summary?year=${selectedYear}&month=${selectedMonth}`;
      const summaryResp = await axios.get(summaryUrl, { headers: buildHeaders() });
      const summary = summaryResp.data || {};
      setTotals({
        totalDebit: summary.total_invoiced || 0,
        totalCredit: summary.total_payments || 0,
        netBalance: (summary.total_invoiced || 0) - (summary.total_payments || 0)
      });
    } catch (err) {
      setError("Failed to fetch monthly report");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnualReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const breakdownUrl = `${baseURL}/payments/billing/annual-monthly-breakdown?year=${selectedYear}`;
      const resp = await axios.get(breakdownUrl, { headers: buildHeaders() });
      const data = resp.data?.data || resp.data || [];
      setAnnualBreakdown(Array.isArray(data) ? data : []);

      const summaryUrl = `${baseURL}/payments/billing/annual-summary?year=${selectedYear}`;
      const summaryResp = await axios.get(summaryUrl, { headers: buildHeaders() });
      const summary = summaryResp.data || {};
      setTotals({
        totalDebit: summary.total_invoiced || 0,
        totalCredit: summary.total_payments || 0,
        netBalance: (summary.total_invoiced || 0) - (summary.total_payments || 0)
      });
    } catch (err) {
      setError("Failed to fetch annual report");
      setAnnualBreakdown([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (activeTab !== "annual" && payments.length === 0) return;
    if (activeTab === "annual" && annualBreakdown.length === 0) return;

    const printWindow = window.open("", "_blank");
    const date = new Date().toLocaleDateString("en-IN");

    let reportTitle = "Patient Billing Report";
    let subHeader = `Patient ID: <b>${patientId || "All Patients"}</b> | Date Range: <b>${formatDate(startDate)}</b> to <b>${formatDate(endDate)}</b>`;

    if (activeTab === "monthly") {
      reportTitle = "Monthly Billing Report";
      subHeader = `Report for: <b>${months[selectedMonth - 1]} ${selectedYear}</b>`;
    } else if (activeTab === "annual") {
      reportTitle = "Annual Financial Report";
      subHeader = `Performance for Year: <b>${selectedYear}</b>`;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${reportTitle} - Amrutdhara</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; }
            .report-container { max-width: 900px; margin: auto; }
            .header { text-align: center; border-bottom: 3px solid #1a3f3a; padding-bottom: 20px; margin-bottom: 30px; }
            .hospital-name { font-size: 32px; font-weight: bold; color: #1a3f3a; margin: 0; }
            .report-title { font-size: 20px; text-transform: uppercase; letter-spacing: 2px; color: #666; margin-top: 5px; }
            .summary-section { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; }
            .summary-card { background: #f8f9fa; padding: 20px; border-radius: 12px; text-align: center; border: 1px solid #dee2e6; }
            .summary-label { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #6c757d; margin-bottom: 5px; }
            .summary-value { font-size: 20px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #1a3f3a; color: white; text-align: left; padding: 12px; font-size: 13px; text-transform: uppercase; }
            td { padding: 12px; border-bottom: 1px solid #dee2e6; font-size: 13px; }
            .debit { color: #dc3545; font-weight: 600; }
            .credit { color: #28a745; font-weight: 600; }
            .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #999; border-top: 1px solid #eee; padding-top: 20px; }
            .grid-item { border: 1px solid #eee; padding: 15px; text-align: center; border-radius: 8px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <h1 class="hospital-name">Amrutdhara Wellness</h1>
              <div class="report-title">${reportTitle}</div>
              <p style="margin-top: 10px;">${subHeader}</p>
            </div>

            <div class="summary-section">
              <div class="summary-card">
                <div class="summary-label">${activeTab === "annual" ? "Yearly Revenue" : "Total Debit"}</div>
                <div class="summary-value" style="color: #dc3545;">${formatCurrency(totals.totalDebit)}</div>
              </div>
              <div class="summary-card">
                <div class="summary-label">${activeTab === "annual" ? "Yearly Collections" : "Total Credit"}</div>
                <div class="summary-value" style="color: #28a745;">${formatCurrency(totals.totalCredit)}</div>
              </div>
              <div class="summary-card">
                <div class="summary-label">${activeTab === "annual" ? "Yearly Balance" : "Net Balance"}</div>
                <div class="summary-value" style="color: #1a3f3a;">${formatCurrency(totals.netBalance)}</div>
              </div>
            </div>

            ${activeTab === "annual" ? `
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th style="text-align: right;">Revenue (Debit)</th>
                    <th style="text-align: right;">Collections (Credit)</th>
                    <th style="text-align: right;">Outstanding (Balance)</th>
                    <th style="text-align: right;">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  ${months.map((m, i) => {
      const data = annualBreakdown.find(d => d.month === (i + 1));
      return `
                      <tr>
                        <td><b>${m}</b></td>
                        <td style="text-align: right;" class="debit">${data ? formatCurrency(data.monthly_invoiced) : "—"}</td>
                        <td style="text-align: right;" class="credit">${data ? formatCurrency(data.monthly_payments) : "—"}</td>
                        <td style="text-align: right;"><b>${data ? formatCurrency(data.monthly_balance) : "—"}</b></td>
                        <td style="text-align: right; opacity: 0.6;">${data?.transactions_count || 0} Trx</td>
                      </tr>
                    `;
    }).join('')}
                </tbody>
                <tfoot style="background: #f8f9fa;">
                  <tr>
                    <td><b>ANNUAL TOTAL</b></td>
                    <td style="text-align: right;" class="debit"><b>${formatCurrency(totals.totalDebit)}</b></td>
                    <td style="text-align: right;" class="credit"><b>${formatCurrency(totals.totalCredit)}</b></td>
                    <td style="text-align: right; color: #1a3f3a;"><b>${formatCurrency(totals.netBalance)}</b></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            ` : `
              <table>
                <thead>
                    <tr>
                      <th>Date</th>
                      <th>Patient Name</th>
                      <th>Transaction ID</th>
                      <th style="text-align: right;">Debit</th>
                      <th style="text-align: right;">Credit</th>
                      <th style="text-align: right;">Balance</th>
                    </tr>
                </thead>
                <tbody>
                  ${payments.map(p => {
      const debit = Number(p.CONSULTING_CHARGES || 0) + Number(p.PANCHKARMA_CHARGES || 0) + Number(p.MEDICINE_CHARGES || 0);
      const credit = Number(p.CURRENT_PAYMENT || 0);
      const balance = Number(p.NEW_BALANCE || 0);
      return `
                      <tr>
                        <td>${formatDate(p.LAST_UPDATE_DT)}</td>
                        <td>${p.PATIENT_NAME || p.PATIENT_ID || '-'}</td>
                        <td style="font-family: monospace; font-size: 11px;">#${p.TRANSACTION_ID}</td>
                        <td style="text-align: right;" class="debit">${debit > 0 ? formatCurrency(debit) : "—"}</td>
                        <td style="text-align: right;" class="credit">${credit > 0 ? formatCurrency(credit) : "—"}</td>
                        <td style="text-align: right;"><b>${formatCurrency(balance)}</b></td>
                      </tr>
                    `;
    }).join('')}
                </tbody>
              </table>
            `}

            <div class="footer">
              <p>Generated on ${date} | Amrutdhara Clinic System</p>
            </div>

            <center class="no-print" style="margin-top: 30px;">
              <button onclick="window.print()" style="padding: 12px 24px; background: #1a3f3a; color: white; border: none; border-radius: 8px; font-weight: bold; cursor: pointer;">Print Report</button>
            </center>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const tableHeaderStyle = {
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    color: theme.text,
    borderColor: theme.border,
    fontFamily: theme.fonts?.main || "inherit",
    fontWeight: "700",
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.1em",
    padding: "1.25rem 2rem",
    borderBottom: `1px solid ${theme.border}`,
    textAlign: "left"
  };

  const tableCellStyle = {
    backgroundColor: "transparent",
    color: theme.text,
    borderColor: theme.border,
    fontFamily: theme.fonts?.main || "inherit",
    fontSize: "14px",
    padding: "1.25rem 2rem",
    border: "none",
    borderBottom: `1px solid ${theme.border}`
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 5; i++) yearOptions.push(currentYear - i);

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors duration-500" style={{ fontFamily: theme.fonts?.main }}>
      <div className="max-w-[1400px] mx-auto">

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col md:flex-row justify-between items-end gap-6"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-serif mb-2" style={{ color: theme.text, fontFamily: theme.fonts?.serif }}>
              {activeTab === "patient" ? "Patient Billing Report" :
                activeTab === "monthly" ? "Monthly Billing Report" : "Annual Financial Report"}
            </h2>
            <p className="text-lg opacity-80" style={{ color: theme.textSecondary }}>
              {activeTab === "patient" ? "Detailed transaction history and financial summary for individual patients" :
                activeTab === "monthly" ? `Complete billing summary for ${months[selectedMonth - 1]} ${selectedYear}` :
                  `Annual financial performance and monthly breakdown for ${selectedYear}`}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              disabled={payments.length === 0 && activeTab !== "annual"}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all font-main disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Printer size={18} />
              Print Report
            </button>
          </div>
        </motion.div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-8 p-1 rounded-2xl w-fit" style={{ backgroundColor: theme.card + '50', border: `1px solid ${theme.border}30` }}>
          {[
            { id: "patient", label: "Patient Detail", icon: UserIcon },
            { id: "monthly", label: "Monthly Report", icon: Calendar },
            { id: "annual", label: "Annual Report", icon: IndianRupee }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
              style={{
                backgroundColor: activeTab === tab.id ? theme.primary : 'transparent',
                color: activeTab === tab.id ? 'white' : theme.text,
                boxShadow: activeTab === tab.id ? `0 10px 20px -5px ${theme.primary}50` : 'none'
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          {/* Filters Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 p-6 rounded-[2rem] h-fit shadow-xl"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <div className="flex items-center gap-2 mb-6" style={{ color: theme.primary }}>
              <Filter size={18} />
              <h3 className="font-bold text-lg" style={{ fontFamily: theme.fonts?.main }}>Report Filters</h3>
            </div>

            <div className="space-y-6">
              {activeTab === "patient" ? (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>Patient Search</label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" size={18} style={{ color: theme.text }} />
                      <input
                        type="text"
                        placeholder="Search ID or Name"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{
                          backgroundColor: theme.inputBg,
                          borderColor: theme.border,
                          color: theme.text,
                          '--tw-ring-color': theme.primary + '30'
                        }}
                        value={patientId}
                        onChange={(e) => setPatientId(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="block text-xs font-bold uppercase tracking-wider opacity-60" style={{ color: theme.textSecondary }}>Quick Range</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['weekly', 'monthly', 'yearly', 'custom'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setRange(type)}
                          className="py-2.5 rounded-xl text-xs font-bold uppercase border transition-all hover:scale-[1.02] active:scale-[0.98]"
                          style={{
                            backgroundColor: reportType === type ? theme.primary : 'transparent',
                            color: reportType === type ? 'white' : theme.text,
                            borderColor: reportType === type ? theme.primary : theme.border
                          }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 pt-2">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>Start Date</label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setReportType("custom");
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>End Date</label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setReportType("custom");
                        }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={fetchPayments}
                    disabled={loading}
                    className="w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: theme.primary }}
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
                    {loading ? "Fetching..." : "Fetch Report"}
                  </button>
                </>
              ) : activeTab === "monthly" ? (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>Select Year</label>
                      <select
                        className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      >
                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>Select Month</label>
                      <select
                        className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      >
                        {months.map((m, idx) => (
                          <option key={m} value={idx + 1}>{m}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={fetchMonthlyReport}
                      disabled={loading}
                      className="w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                      style={{ backgroundColor: theme.primary }}
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <FileText size={20} />}
                      Generate Report
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-60" style={{ color: theme.textSecondary }}>Select Year</label>
                      <select
                        className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 transition-all font-medium"
                        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      >
                        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>

                    <button
                      onClick={fetchAnnualReport}
                      disabled={loading}
                      className="w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                      style={{ backgroundColor: theme.primary }}
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <IndianRupee size={20} />}
                      Generate Annual Report
                    </button>
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-600 border border-red-100">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <p className="text-xs font-medium">{error}</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Main Content Column */}
          <div className="lg:col-span-3 space-y-8">

            {/* Summary Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SummaryCard
                label={activeTab === "annual" ? "Annual Revenue" : "Total Debit"}
                value={totals.totalDebit}
                icon={ArrowUpRight}
                color={theme.danger}
                theme={theme}
              />
              <SummaryCard
                label={activeTab === "annual" ? "Annual Collections" : "Total Credit"}
                value={totals.totalCredit}
                icon={ArrowDownRight}
                color={theme.success}
                theme={theme}
              />
              <SummaryCard
                label={activeTab === "annual" ? "Annual Balance" : "Net Balance"}
                value={totals.netBalance}
                icon={Wallet}
                color={theme.primary}
                theme={theme}
              />
            </div>

            {/* Annual Breakdown Table - Auditing Format */}
            {activeTab === "annual" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-[2.5rem] overflow-hidden shadow-2xl border"
                style={{ backgroundColor: theme.card, borderColor: theme.border }}
              >
                <div className="p-8 border-b flex justify-between items-center" style={{ borderColor: theme.border }}>
                  <h3 className="font-bold text-xl" style={{ color: theme.text, fontFamily: theme.fonts?.main }}>Financial Audit Summary - {selectedYear}</h3>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-primary/10" style={{ color: theme.primary }}>
                      Official Record
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse m-0">
                    <thead>
                      <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                        <th style={{ ...tableHeaderStyle, width: '20%' }}>Month</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '20%' }}>Revenue (Debit)</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '20%' }}>Collections (Credit)</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '20%' }}>Outstanding (Balance)</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '20%' }}>Volume</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: theme.border }}>
                      {months.map((month, idx) => {
                        const mData = annualBreakdown.find(m => m.month === (idx + 1));
                        return (
                          <motion.tr
                            key={month}
                            whileHover={{ backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}
                            className="transition-colors"
                          >
                            <td style={tableCellStyle}>
                              <span className="font-bold">{month}</span>
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                              <span className="font-semibold" style={{ color: theme.danger }}>
                                {mData ? formatCurrency(mData.monthly_invoiced) : "—"}
                              </span>
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                              <span className="font-semibold" style={{ color: theme.success }}>
                                {mData ? formatCurrency(mData.monthly_payments) : "—"}
                              </span>
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                              <span className="font-bold" style={{ color: theme.primary }}>
                                {mData ? formatCurrency(mData.monthly_balance) : "—"}
                              </span>
                            </td>
                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                              <span className="text-xs font-mono opacity-40">{mData?.transactions_count || 0} Trx</span>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: theme.primary + '10' }}>
                        <td style={{ ...tableCellStyle, borderBottom: 'none' }} className="font-black uppercase tracking-widest text-xs">Annual Total</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right', borderBottom: 'none', color: theme.danger }} className="font-black text-lg">{formatCurrency(totals.totalDebit)}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right', borderBottom: 'none', color: theme.success }} className="font-black text-lg">{formatCurrency(totals.totalCredit)}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right', borderBottom: 'none', color: theme.primary }} className="font-black text-lg">{formatCurrency(totals.netBalance)}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right', borderBottom: 'none' }}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Table Section */}
            {(activeTab !== "annual") && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
                style={{ backgroundColor: theme.card, borderColor: theme.border }}
              >
                <div className="p-8 border-b flex justify-between items-center" style={{ borderColor: theme.border }}>
                  <h3 className="font-bold text-xl" style={{ color: theme.text, fontFamily: theme.fonts?.main }}>Transaction History</h3>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.success }}></span>
                    <span className="text-xs font-bold opacity-60 uppercase tracking-widest" style={{ color: theme.textSecondary }}>
                      {payments.length} Records
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse m-0" style={{ backgroundColor: theme.inputBg }}>
                    <thead>
                      <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                        <th style={{ ...tableHeaderStyle, width: '15%' }}>Trans. Date</th>
                        <th style={{ ...tableHeaderStyle, width: '20%' }}>Patient Name</th>
                        <th style={{ ...tableHeaderStyle, width: '15%' }}>Invoice ID</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '15%' }}>Debit</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '15%' }}>Credit</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'right', width: '15%' }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: theme.border }}>
                      {payments.length === 0 ? (
                        <tr className="border-0" style={{ backgroundColor: 'transparent' }}>
                          <td colSpan={5} style={{ ...tableCellStyle, textAlign: 'center', padding: '6rem 2rem', borderBottom: 'none' }}>
                            <div className="flex flex-col items-center opacity-30">
                              <FileText size={64} className="mb-4" style={{ color: theme.text }} />
                              <p className="text-lg font-bold" style={{ color: theme.text }}>
                                {loading ? "Accessing Records..." : "Search for a Patient ID to generate report"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        payments.map((p, idx) => {
                          const debit = Number(p.CONSULTING_CHARGES || 0) + Number(p.PANCHKARMA_CHARGES || 0) + Number(p.MEDICINE_CHARGES || 0);
                          const credit = Number(p.CURRENT_PAYMENT || 0);
                          const balance = Number(p.NEW_BALANCE || 0);
                          return (
                            <motion.tr
                              key={p.TRANSACTION_ID + p.LAST_UPDATE_DT}
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.02 }}
                              className="group transition-colors"
                              style={{
                                borderBottom: `1px solid ${theme.border}`,
                                backgroundColor: isDarkMode ? theme.inputBg : 'inherit'
                              }}
                              whileHover={{
                                backgroundColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.03)"
                              }}
                            >
                              <td style={tableCellStyle}>
                                <span className="font-semibold">{formatDate(p.LAST_UPDATE_DT)}</span>
                              </td>
                              <td style={tableCellStyle}>
                                <span className="font-semibold">{p.PATIENT_NAME || p.PATIENT_ID || '-'}</span>
                              </td>
                              <td style={tableCellStyle}>
                                <span className="font-mono text-xs opacity-50">#{p.TRANSACTION_ID}</span>
                              </td>
                              <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                <span className="font-bold" style={{ color: theme.danger }}>
                                  {debit > 0 ? formatCurrency(debit) : "—"}
                                </span>
                              </td>
                              <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                <span className="font-bold" style={{ color: theme.success }}>
                                  {credit > 0 ? formatCurrency(credit) : "—"}
                                </span>
                              </td>
                              <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                <span className="font-bold" style={{ color: theme.primary }}>
                                  {formatCurrency(balance)}
                                </span>
                              </td>
                            </motion.tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t text-center text-sm opacity-50" style={{ borderColor: theme.border, color: theme.textSecondary }}>
          © {new Date().getFullYear()} Amrutdhara Clinic System • Financial Reporting
        </div>
      </div>
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, color, theme }) => (
  <motion.div
    whileHover={{ y: -5 }}
    className="p-6 rounded-[2rem] relative overflow-hidden group transition-all shadow-xl shadow-black/5"
    style={{ backgroundColor: theme.card }}
  >
    <div className="relative z-10">
      <div className="flex items-center gap-2 mb-4 opacity-70" style={{ color: theme.textSecondary }}>
        <Icon size={16} style={{ color: color }} />
        <span className="text-xs font-bold uppercase tracking-widest">{label}</span>
      </div>
      <div className="text-2xl font-bold" style={{ color: theme.text }}>
        {formatCurrency(value)}
      </div>
    </div>
    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
      <Icon size={80} style={{ color: color }} />
    </div>
  </motion.div>
);

const UserIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);
